import {
    DamImageRequest,
    ImageRequestV2,
    ImageServiceV2,
    RdmImageRequest,
    Scene7ImageFormat,
    Scene7ImageRequest
} from '@volkswagen-onehub/image-service';
import {
    MediaAspectRatio,
    MediaAssetImageModel,
    RemoteDynamicMediaAssetImageModel,
    Scene7MediaAssetImageModel
} from '../../generated/core';
import {focalPointMap} from './focalPointMap';
import {AspectRatioValue} from '@volkswagen-onehub/components-core';
import {FocalPoint} from '../d6/components/focal-point';
import {aspectRatioMap} from './aspectRatioMap';

export interface ImageResourceSet {
    readonly src: string;
    readonly srcSet: string;
    readonly srcSetWebP: string;
}

const DEFAULT_IMAGE_WIDTH = 800;

export function getImageResourceSet(
    imageService: ImageServiceV2,
    props: MediaAssetImageModel,
    enableSmartImaging: boolean,
    transparent?: boolean,
    mediaAspectRatio?: MediaAspectRatio
): ImageResourceSet {
    let src: string;
    let srcSet = '';
    let srcSetWebP = '';
    const {focalPoint: mediaFocalPoint} = props;

    const aspectRatio = mediaAspectRatio
        ? aspectRatioMap[mediaAspectRatio]
        : undefined;
    const focalPoint = mediaFocalPoint
        ? focalPointMap[mediaFocalPoint]
        : undefined;

    const useSmartImaging = enableSmartImaging && !transparent;

    const pngRequest = createImageRequest(props, {
        aspectRatio,
        focalPoint,
        format: 'png',
        transparent: !!transparent,
        useSmartImaging
    });

    srcSet = imageService.getImageSrcSet(pngRequest);
    src = imageService.getImageURL(pngRequest, DEFAULT_IMAGE_WIDTH);
    if (!useSmartImaging) {
        const webpRequest = createImageRequest(props, {
            aspectRatio,
            focalPoint,
            format: 'webp',
            transparent: !!transparent,
            useSmartImaging
        });
        srcSetWebP = imageService.getImageSrcSet(webpRequest);
    }

    return {src, srcSet, srcSetWebP};
}

function createImageRequest(
    model: MediaAssetImageModel,
    options: {
        aspectRatio?: AspectRatioValue;
        width?: number;
        focalPoint?: FocalPoint;
        format: 'webp' | 'png';
        transparent: boolean;
        useSmartImaging: boolean;
    }
): ImageRequestV2 {
    const damSrc = model.damSrc;

    if (model.imageSourceType === 'Scene7' || !model.imageSourceType) {
        var scene7Model: Scene7MediaAssetImageModel =
            model as Scene7MediaAssetImageModel;
        if (!scene7Model.validScene7Asset || !scene7Model.scene7File) {
            return {
                contentFile: damSrc,
                imageSourceType: 'dam'
            } as DamImageRequest;
        }
        const request: Scene7ImageRequest = {
            scene7File: scene7Model.scene7File,
            imageSourceType: 'scene7',
            contentFile: '',
            metaData: {
                originalHeight: scene7Model.originalHeight || 0,
                originalWidth: scene7Model.originalWidth || 0
            },
            params: {
                aspectRatio: options.aspectRatio,
                focalPoint: options.focalPoint,
                useSmartImaging: options.useSmartImaging
            }
        };
        if (!options.useSmartImaging) {
            const params = request.params;
            if (params) {
                params.format = (
                    options.transparent
                        ? options.format + '-alpha'
                        : options.format
                ) as Scene7ImageFormat;
            }
        }
        return request as Scene7ImageRequest;
    }
    if (model.imageSourceType === 'RDM') {
        var rdmModel = model as RemoteDynamicMediaAssetImageModel;

        //TODO preferwebp parameter better?
        return {
            assetId: rdmModel.assetId,
            imageSourceType: 'remote-dynamic-media',
            metaData: {
                originalHeight: rdmModel.originalHeight,
                originalWidth: rdmModel.originalWidth
            },
            repositoryId: rdmModel.repositoryId,
            params: {
                seoName: rdmModel.seoName,
                aspectRatio: options.aspectRatio,
                focalPoint: options.focalPoint,
                format: options.format
            }
        } as RdmImageRequest;
    }

    return {
        imageSourceType: 'dam',
        contentFile: damSrc || ''
    };
}
